import React from "react";
import moment from "moment";
import { Col, Form, Row, Space } from "antd";
import { useTranslation } from "react-i18next";
import AdnCard from "../common/adnCard/AdnCard";
import AdnDatePicker from "../common/adnDatePicker/AdnDatePicker";
import AdnDropdown from "../common/adnDropdown/AdnDropdown";
import AdnAutoComplete from "../common/adnAutoComplete/AdnAutoComplete";
import AdnInput from "../common/adnInput/AdnInput";
import AdnPageTitle from "../common/adnPageTitle/AdnPageTitle";
import AdnTextArea from "../common/adnTextArea/AdnTextArea";
import AdnFormOptions from "../common/adnFormOptions/AdnFormOptions";
import AdnAddButton from "../common/adnAddButton/AdnAddButton";
import AdnTable from "../common/adnTable/AdnTable";
import { dateFormat } from "../common/GeneralConfigurations";
import { EditOutlined } from "@ant-design/icons";

export const CriticalSituationForm = ({
  form,
  initValues,
  isManagementHumanCapital,
  statusOptions,
  managers,
  readOnly,
  employeeReadOnly,
  loading,
  submitLoading,
  title,
  handleCancel,
  handleSubmitForm,
  handleSearchEmployee,
  handleSelectEmployee,
  employeesList,
  handleStatusChange,
  handleAddCriticalSituationFollowup,
  criticalSituationFollowups,
  handleEditCriticalSituationFollowup,
}) => {
  const { t } = useTranslation();
  const followupColumns = [
    {
      title: t("criticalSituationForm.columns.followupDate"),
      dataIndex: "followupDate",
      key: "followupDate",
      width: "5%",
      render: (text) => (text ? moment(text).format(dateFormat) : undefined),
    },
    {
      title: t("criticalSituationForm.columns.description"),
      dataIndex: "description",
      key: "description",
      width: "50%",
    },
  ];

  if (isManagementHumanCapital === true) {
    followupColumns.push({
      title: t("criticalSituationForm.columns.action"),
      key: "action",
      width: "5%",
      render: (record) => (
        <Space style={{ display: "flex", justifyContent: "center" }}>
          <EditOutlined
            onClick={() => handleEditCriticalSituationFollowup(record.id)}
          />
        </Space>
      ),
    });
  }

  const disableReportDateDate = (current) => {
    // accepts dates only till today
    return current && current > moment().startOf("day");
  };

  const disableFollowupDateDate = (current) => {
    // accepts dates only from today on
    return current && current <= moment().startOf("day");
  };

  return (
    <>
      <AdnPageTitle title={title} />
      <AdnCard loading={loading}>
        <Form
          form={form}
          name="criticalSituationForm"
          layout="vertical"
          onFinish={handleSubmitForm}
          initialValues={initValues}
        >
          <Form.Item name="employeeId" style={{ display: "none" }}>
            <input type="hidden" />
          </Form.Item>

          <Row gutter={16}>
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 12 }}
              xxl={{ span: 12 }}
              key="employee"
              className="gutter-row"
            >
              {(readOnly || employeeReadOnly) && (
                <AdnInput
                  name="employee"
                  label={t("criticalSituationForm.labels.consultant")}
                  readOnly={true}
                />
              )}
              {!readOnly && !employeeReadOnly && (
                <AdnAutoComplete
                  name="employee"
                  valueId="name"
                  label={t("criticalSituationForm.labels.consultant")}
                  placeholder={t(
                    "criticalSituationForm.placeholders.selectConsultant"
                  )}
                  handleSearch={handleSearchEmployee}
                  handleSelect={handleSelectEmployee}
                  resultList={employeesList}
                  rules={[
                    {
                      required: true,
                      message: t(
                        "criticalSituationForm.validation.consultantRequired"
                      ),
                    },
                  ]}
                  allowClear={true}
                />
              )}
            </Col>
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 4 }}
              xxl={{ span: 4 }}
              key="reportDate"
              className="gutter-row"
            >
              <AdnDatePicker
                label={t("criticalSituationForm.labels.reportDate")}
                name="reportDate"
                placeholder={t("criticalSituationForm.placeholders.selectDate")}
                disabledDate={disableReportDateDate}
                disabled={readOnly}
                rules={[
                  {
                    required: true,
                    message: t(
                      "criticalSituationForm.validation.reportDateRequired"
                    ),
                  },
                ]}
                format={dateFormat}
                allowClear={false}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 12 }}
              xxl={{ span: 12 }}
              key="origin"
              className="gutter-row"
            >
              <AdnInput
                label={t("criticalSituationForm.labels.origin")}
                name="origin"
                placeholder={t(
                  "criticalSituationForm.placeholders.criticalSituationOrigin"
                )}
                maxLength={100}
                readOnly={readOnly}
                rules={[
                  {
                    required: true,
                    message: t(
                      "criticalSituationForm.validation.originRequired"
                    ),
                  },
                ]}
              />
            </Col>
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 4 }}
              xxl={{ span: 4 }}
              key="managerId"
              className="gutter-row"
            >
              <AdnDropdown
                name="managerId"
                label={t("criticalSituationForm.labels.manager")}
                placeholder={t(
                  "criticalSituationForm.placeholders.selectManager"
                )}
                options={managers}
                valueIsId={true}
                disabled={readOnly}
                allowClear={true}
              />
            </Col>
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 4 }}
              xxl={{ span: 4 }}
              key="followupDate"
              className="gutter-row"
            >
              <AdnDatePicker
                label={t("criticalSituationForm.labels.nextFollowUpDate")}
                name="followupDate"
                placeholder={t("criticalSituationForm.placeholders.selectDate")}
                disabledDate={disableFollowupDateDate}
                disabled={readOnly}
                format={dateFormat}
              />
            </Col>
            {initValues.id > 0 && (
              <Col
                xs={{ span: 24 }}
                sm={{ span: 24 }}
                md={{ span: 24 }}
                lg={{ span: 24 }}
                xl={{ span: 4 }}
                xxl={{ span: 4 }}
                key="statusId"
                className="gutter-row"
              >
                <AdnDropdown
                  name="statusId"
                  label={t("criticalSituationForm.labels.status")}
                  placeholder={t(
                    "criticalSituationForm.placeholders.selectStatus"
                  )}
                  handleChange={handleStatusChange}
                  options={statusOptions}
                  disabled={initValues.id === 0}
                  rules={[
                    {
                      required: true,
                      message: t(
                        "criticalSituationForm.validation.statusRequired"
                      ),
                    },
                  ]}
                />
              </Col>
            )}
          </Row>

          <Row gutter={16}>
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 24 }}
              xxl={{ span: 24 }}
              key="description"
              className="gutter-row"
            >
              <AdnTextArea
                name="description"
                label={t("criticalSituationForm.labels.description")}
                maxLength={1500}
                showCount={true}
                disabled={readOnly}
                placeholder={t(
                  "criticalSituationForm.placeholders.describeCriticalSituation"
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      "criticalSituationForm.validation.descriptionRequired"
                    ),
                  },
                ]}
                rows={4}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 24 }}
              xxl={{ span: 24 }}
              key="correctiveAction"
              className="gutter-row"
            >
              <AdnTextArea
                name="correctiveAction"
                label={t("criticalSituationForm.labels.correctiveAction")}
                maxLength={500}
                showCount={true}
                disabled={readOnly}
                placeholder={t(
                  "criticalSituationForm.placeholders.describeCorrectiveAction"
                )}
                rows={3}
              />
            </Col>
          </Row>

          <Row gutter={16} style={{ paddingTop: "20px" }}>
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 24 }}
              xxl={{ span: 24 }}
              key="buttons"
              className="gutter-row"
            >
              <AdnFormOptions
                handleCancel={handleCancel}
                loading={submitLoading}
              />
            </Col>
          </Row>
        </Form>

        {initValues.id > 0 && (
          <>
            <AdnPageTitle title={t("criticalSituationForm.title")} />
            {isManagementHumanCapital === true && readOnly === false && (
              <Row gutter={16}>
                <Col
                  xs={{ span: 24 }}
                  sm={{ span: 24 }}
                  md={{ span: 8 }}
                  lg={{ span: 8 }}
                  xl={{ span: 3 }}
                  xxl={{ span: 3 }}
                >
                  <AdnAddButton
                    handleClick={handleAddCriticalSituationFollowup}
                    name={t("criticalSituationForm.buttons.add")}
                  />
                </Col>
              </Row>
            )}

            <Row>
              <Col span={24}>
                <AdnTable
                  columns={followupColumns}
                  data={criticalSituationFollowups}
                  showPagination={false}
                />
              </Col>
            </Row>
          </>
        )}
      </AdnCard>
    </>
  );
};
